import { PlayerCard } from "../../../../../../components/PlayerCard";
import type { Player } from "../../../../teams/types/teams";
import type { IMatchDetails, IPlayersTeamSquad, ITeamsSquad, ISubsitutes} from "./types/matchDetails.d";
import { Images } from "../../../../../../utils/imageConstants";
import { formatPlayerRoleLabel, getPlayerImageUrl } from "../../../../../../helper/helper";

interface PlayersProps {
  matchSummary: IMatchDetails | undefined;
  matchTab:string;
  teamOnePlayers: IPlayersTeamSquad | undefined;
  teamTwoPlayers: IPlayersTeamSquad | undefined;
  matchStatus?: string;
}

const MatchesPlayers = ({ matchSummary, teamOnePlayers, teamTwoPlayers, matchStatus }: PlayersProps) => {
  const isUpcoming = matchStatus === "UPCOMING" || matchStatus === "SCHEDULED";

  const getPlayerRole = (playerRole: string | null | undefined): string => {
    if (!playerRole) return "Player";
    const roleMap: { [key: string]: string } = {
      "BAT": "Batter",
      "BOWL": "Bowler",
      "WK": "Wicketkeeper",
      "AR": "All-Rounder",
      "ALL": "All-Rounder",
    };
    
    // Check if role contains common patterns
    const roleUpper = playerRole.toUpperCase();
    if (roleUpper.includes("BATTER") || roleUpper.includes("BAT")) {
      if (roleUpper.includes("TOP") || roleUpper.includes("OPEN")) return "Top Order Batter";
      if (roleUpper.includes("MIDDLE")) return "Middle Order Batter";
      if (roleUpper.includes("WK") || roleUpper.includes("WICKET")) return "Wicketkeeper Batter";
      return "Batter";
    }
    if (roleUpper.includes("BOWL") || roleUpper.includes("BOWLER")) return "Bowler";
    if (roleUpper.includes("WK") || roleUpper.includes("WICKET")) return "Wicketkeeper";
    if (roleUpper.includes("ALL") || roleUpper.includes("ROUNDER")) return "All-Rounder";
    
    return roleMap[roleUpper] || formatPlayerRoleLabel(playerRole);
  };

  const getPlayersToDisplay = (players: IPlayersTeamSquad | undefined): (ITeamsSquad | ISubsitutes)[] => {
    if (!players) return [];
    if (players.playingSquad && players.playingSquad.length > 0) {
      return players.playingSquad;
    }
    return players.bench || [];
  };


  if (isUpcoming) {
    const teamOnePlayersList = getPlayersToDisplay(teamOnePlayers);
    const teamTwoPlayersList = getPlayersToDisplay(teamTwoPlayers);

    return (
      <section aria-label="Match squads">
        <div className="upcoming-squads-wrapper">
          {/* Team 1 Column */}
          <div className="upcoming-squad-column">
            <div className="upcoming-squad-header">
              <p>{matchSummary?.teamOne?.teamName?.toUpperCase() ?? "TEAM 1"}</p>
            </div>
            <div className="upcoming-squad-list">
              {teamOnePlayersList.map((player, idx) => (
                <div key={player?.playerId || idx} className="upcoming-squad-player-item">
                  <div className="upcoming-squad-player-info">
                    <div className="upcoming-squad-player-image-wrapper">
                      <img 
                        src={getPlayerImageUrl(player?.imageUri) || Images.player_profile_image} 
                        alt={player?.playerFirstName || "Player"} 
                      />
                    </div>
                    <div className="upcoming-squad-player-details">
                      <p className="upcoming-squad-player-name">
                        {player?.playerFirstName} {player?.playerLastName}
                      </p>
                      <p className="upcoming-squad-player-role">
                        {getPlayerRole(player?.playerRole)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Team 2 Column */}
          <div className="upcoming-squad-column">
            <div className="upcoming-squad-header">
              <p>{matchSummary?.teamTwo?.teamName?.toUpperCase() ?? "TEAM 2"}</p>
            </div>
            <div className="upcoming-squad-list">
              {teamTwoPlayersList.map((player, idx) => (
                <div key={player?.playerId || idx} className="upcoming-squad-player-item">
                  <div className="upcoming-squad-player-info">
                    <div className="upcoming-squad-player-image-wrapper no-border">
                      <img 
                        src={getPlayerImageUrl(player?.imageUri) || Images.player_profile_image} 
                        alt={player?.playerFirstName || "Player"} 
                      />
                    </div>
                    <div className="upcoming-squad-player-details">
                      <p className="upcoming-squad-player-name">
                        {player?.playerFirstName} {player?.playerLastName}
                      </p>
                      <p className="upcoming-squad-player-role">
                        {getPlayerRole(player?.playerRole)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const batters: Player[] = [
    { id: "p1",  name: "MS DHONI",         role: "WK-Batter", tags: ["C", "WK"] },
    { id: "p2",  name: "VIRAT KOHLI",      role: "Batter",    tags: ["VC"] },
    { id: "p3",  name: "ROHIT SHARMA",     role: "Batter" },
    { id: "p4",  name: "SHREYAS IYER",     role: "Batter" },
    { id: "p5",  name: "KL RAHUL",         role: "WK-Batter" },
    { id: "p10", name: "SHIKHAR DHAWAN",   role: "Batter" },
    { id: "p11", name: "SURYAKUMAR YADAV", role: "Batter" },
  ];

  return (
    <section aria-label="Match players">
      <div className="players-wrapper">
        {/* Team 1 */}
        <section
          className="team-one"
          aria-labelledby="team-one-name"
          aria-describedby="team-one-description"
        >
          <h2 id="team-one-name" className="team-name">
            {matchSummary?.teamOne?.teamName ?? "-"}
          </h2>

          <section className="playing-teams" aria-label="Playing XI">
            {/* keep <p> for styling, but give it heading semantics */}
            <p role="heading" aria-level={3}>Playing Xl</p>
            <div className="players-cards" role="list" aria-label="Players list">
              {getPlayersToDisplay(teamOnePlayers).map((player, idx) => (
                <div key={player?.playerId || idx} role="listitem">
                  <PlayerCard player={player}/>
                </div>
              ))}
            </div>
          </section>

          <section className="playing-teams" aria-label="Substitutes">
            <p role="heading" aria-level={3}>Substitutes</p>
            <div className="players-cards" role="list" aria-label="Substitutes list">
              {teamOnePlayers?.bench?.map((player, idx) => (
                <div key={`${player.playerId}-sub`} role="listitem">
                  <PlayerCard player={player} />
                </div>
              ))}
            </div>
          </section>

          {/* hidden description hook if needed by SRs */}
          <span id="team-one-description" hidden>
            Playing XI and substitutes for Sunrises Hyderabad
          </span>
        </section>

        {/* Team 2 */}
        <section
          className="team-one"
          aria-labelledby="team-two-name"
          aria-describedby="team-two-description"
        >
          <h2 id="team-two-name" className="team-name">
             {matchSummary?.teamTwo?.teamName ?? "-"}
          </h2>

          <section className="playing-teams" aria-label="Playing XI">
            <p role="heading" aria-level={3}>Playing Xl</p>
            <div className="players-cards" role="list" aria-label="Players list">
             {getPlayersToDisplay(teamTwoPlayers).map((player, idx) => (
                <div key={`t2-${player.playerId || idx}`} role="listitem">
                  <PlayerCard player={player}/>
                </div>
              ))}
            </div>
          </section>

          <section className="playing-teams" aria-label="Substitutes">
            <p role="heading" aria-level={3}>Substitutes</p>
            <div className="players-cards" role="list" aria-label="Substitutes list">
              {teamTwoPlayers?.bench?.map((player, idx) => (
                <div key={`t2-${player.playerId}-sub`} role="listitem">
                  <PlayerCard player={player}/>
                </div>
              ))}
            </div>
          </section>

          <span id="team-two-description" hidden>
            Playing XI and substitutes for Royal Challengers Bangalore
          </span>
        </section>
      </div>
    </section>
  );
};

export default MatchesPlayers;
